<template>
  <div class="profile-page">
    <!-- Header -->
    <div class="page-header">
      <h1 class="page-title">Profile</h1>
    </div>

    <div class="profile-content">
      <!-- Profile Card -->
      <div class="profile-card">
        <div class="profile-header">
          <div class="avatar-circle">
            <ImagePreview
              v-if="userStore.userInfo?.avatar"
              :src="userStore.userInfo.avatar"
              :alt="userStore.userInfo?.name || ''"
              width="100%"
              height="100%"
              fit="cover"
              :enable-preview="true"
              :round="true"
            />
            <span v-else>{{ userStore.userInfo?.name?.charAt(0)?.toUpperCase() || userStore.userInfo?.username?.charAt(0)?.toUpperCase() || '' }}</span>
          </div>
          <div class="profile-info">
            <div class="profile-name">{{ userStore.userInfo?.name || userStore.userInfo?.username || '' }}</div>
            <div class="profile-phone">{{ userStore.userInfo?.phone || userStore.userInfo?.phoneNumber || 'N/A' }}</div>
          </div>
          <el-button link class="edit-btn" @click="handleEdit">
            {{ $t('common.edit') }}
          </el-button>
        </div>
      </div>

      <!-- Account Settings -->
      <div class="settings-card">
        <div class="card-section-title">Account Settings</div>
        <div class="menu-list">
          <div class="menu-item" @click="goToChangePassword">
            <div class="menu-left">
              <el-icon class="menu-icon"><Lock /></el-icon>
              <span class="menu-label">Change Password</span>
            </div>
            <el-icon class="menu-arrow"><ArrowRight /></el-icon>
          </div>
          <div class="menu-item" @click="handleNotification">
            <div class="menu-left">
              <el-icon class="menu-icon"><Bell /></el-icon>
              <span class="menu-label">Notification Settings</span>
            </div>
            <el-icon class="menu-arrow"><ArrowRight /></el-icon>
          </div>
        </div>
      </div>

      <!-- System -->
      <div class="settings-card">
        <div class="card-section-title">System</div>
        <div class="menu-list">
          <div class="menu-item" @click="handleFeedback">
            <div class="menu-left">
              <el-icon class="menu-icon"><Document /></el-icon>
              <span class="menu-label">Feedback</span>
            </div>
            <el-icon class="menu-arrow"><ArrowRight /></el-icon>
          </div>
          <div class="menu-item" @click="showAboutDialog = true">
            <div class="menu-left">
              <el-icon class="menu-icon"><InfoFilled /></el-icon>
              <span class="menu-label">About</span>
            </div>
            <el-icon class="menu-arrow"><ArrowRight /></el-icon>
          </div>
        </div>
      </div>

      <!-- Logout Button -->
      <div class="logout-section">
        <el-button type="danger" class="logout-btn" @click="handleLogout">
          Log Out
        </el-button>
      </div>

      <!-- Version -->
      <div class="version-text">Version 1.0.0</div>
    </div>

    <!-- About Dialog -->
    <el-dialog 
      v-model="showAboutDialog" 
      title="About Us"
      width="400px"
      :close-on-click-modal="false"
      class="about-dialog"
      center
    >
      <div class="about-content">
        <div class="about-title">Equestrian Management System</div>
        <div class="about-version">Version: 1.0.0</div>
        <div class="about-company">Yolal Co., Ltd.</div>
        <div class="about-email">yolal@yolal.cn</div>
        <div class="about-website">www.yolal.cn</div>
        <div class="about-description">
          Professional equestrian course booking and management platform
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" @click="showAboutDialog = false">
            OK
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Lock, Bell, Document, InfoFilled, ArrowRight } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'
import ImagePreview from '@/components/ImagePreview.vue'

const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const showAboutDialog = ref(false)

const formatRole = (role) => {
  if (Array.isArray(role)) {
    role = role[0]
  }
  const roleMap = {
    'admin': 'Administrator',
    'teacher': 'Teacher',
    'user': 'User',
    'coach': 'Coach'
  }
  return roleMap[role] || role || 'User'
}

const handleEdit = () => {
  ElMessage.info('Edit profile feature coming soon')
}

const handleNotification = () => {
  ElMessage.info('Notification settings feature coming soon')
}

const handleFeedback = () => {
  ElMessage.info('Feedback feature coming soon')
}

const handleLogout = async () => {
  try {
    await ElMessageBox.confirm(
      t('common.logoutConfirm'),
      t('common.logout'),
      {
        confirmButtonText: t('common.logout'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      }
    )
    
    // Clear user data
    userStore.clearSession()
    
    // Navigate to login
    router.push('/login')
    ElMessage.success(t('common.logoutSuccess'))
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Logout error:', error)
    }
  }
}

const goToChangePassword = () => {
  router.push('/profile/change-password')
}

onMounted(() => {
  // User data is already in store from login
})
</script>

<style scoped lang="scss">
.profile-page {
  height: 100%;
  background-color: #f5f7fa;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .page-header {
    flex-shrink: 0;
    margin-bottom: 20px;

    .page-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #303133;
      text-align: center;
    }
  }

  .profile-content {
    flex: 1;
    overflow-y: auto;
    max-width: 600px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 20px;
    width: 100%;
    box-sizing: border-box;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    }
  }

  .profile-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 16px;

    .profile-header {
      display: flex;
      align-items: center;
      gap: 16px;

      .avatar-circle {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 36px;
        font-weight: 600;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
        overflow: hidden;
        border: 3px solid #ffffff;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 16px rgba(64, 158, 255, 0.4);
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        span {
          user-select: none;
        }
      }

      .profile-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;

        .profile-name {
          font-size: 20px;
          font-weight: 600;
          color: #303133;
        }

        .profile-phone {
          font-size: 14px;
          color: #606266;
        }
      }

      .edit-btn {
        font-size: 14px;
        color: #409eff;
        padding: 4px 8px;
        flex-shrink: 0;

        &:hover {
          color: #66b1ff;
        }
      }
    }
  }

  .settings-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 16px 0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    margin-bottom: 16px;

    .card-section-title {
      padding: 0 20px 12px;
      font-size: 14px;
      font-weight: 600;
      color: #606266;
      border-bottom: 1px solid #e4e7ed;
      margin-bottom: 8px;
    }

    .menu-list {
      .menu-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        cursor: pointer;
        transition: all 0.3s ease;
        user-select: none;
        -webkit-tap-highlight-color: transparent;

        &:hover {
          background-color: #f5f7fa;
        }

        &:active {
          background-color: #f0f2f5;
        }

        .menu-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;

          .menu-icon {
            font-size: 18px;
            color: #409eff;
          }

          .menu-label {
            font-size: 15px;
            color: #303133;
          }
        }

        .menu-arrow {
          font-size: 16px;
          color: #909399;
        }
      }
    }
  }

  .logout-section {
    padding: 0 8px 16px;

    .logout-btn {
      width: 100%;
      height: 48px;
      font-size: 16px;
      font-weight: 500;
      border-radius: 12px;
      background: linear-gradient(135deg, #f56c6c 0%, #e74c3c 100%);
      border: none;
      box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(245, 108, 108, 0.4);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .version-text {
    text-align: center;
    font-size: 13px;
    color: #909399;
    padding: 16px;
    margin-top: 8px;
  }

  .about-dialog {
    :deep(.el-dialog__header) {
      padding: 20px 24px;
      border-bottom: 1px solid #f0f2f5;

      .el-dialog__title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        text-align: center;
        width: 100%;
      }
    }

    :deep(.el-dialog__body) {
      padding: 32px 24px;
    }

    .about-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      text-align: center;

      .about-title {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 4px;
      }

      .about-version {
        font-size: 14px;
        color: #909399;
      }

      .about-company {
        font-size: 15px;
        font-weight: 500;
        color: #606266;
        margin-top: 8px;
      }

      .about-email {
        font-size: 14px;
        color: #409eff;
      }

      .about-website {
        font-size: 14px;
        color: #409eff;
      }

      .about-description {
        font-size: 14px;
        color: #606266;
        margin-top: 16px;
        line-height: 1.6;
      }
    }

    :deep(.el-dialog__footer) {
      padding: 16px 24px;
      border-top: 1px solid #f0f2f5;

      .dialog-footer {
        display: flex;
        justify-content: center;

        .el-button {
          flex: 0 0 120px;
          height: 40px;
          font-size: 15px;
          font-weight: 500;
        }
      }
    }
  }
}
</style>
